// src/components/PendingQuestionsButton.tsx
//
// Phase 3.7.7 — Header (?) icon for pending curator questions.
// Phase 4A — visibility behavior: always visible when feature enabled.
// Phase 4B — visibility behavior REVERTED: only visible when there are
// actually open questions waiting.
//
// Lives in AppShell next to the GPS / notification icons.
// Renders nothing when:
//   - The user is not signed in
//   - The user has disabled questions ("give me a break")
//   - The hook is still loading the first snapshot (no flicker)
//   - There are zero open questions
//
// Phase 4B rationale: the always-visible icon was visually noisy
// and drew taps that landed on intro/outro cards with nothing
// actionable in between. Hiding the icon when count=0 keeps the
// topbar clean; users who want to discover the feature can find
// it through Settings (or wherever the disable toggle is
// surfaced) — the curator still emits questions in the
// background regardless of whether the icon is visible right now.
//
// When the icon DOES show, the badge always reflects the open
// count (1, 2, 3, ..., 9+).

import React, { useState } from 'react';
import { IconButton, Badge } from '@mui/material';
import { HelpOutlineRounded } from '@mui/icons-material';
import { auth } from '../services/firebase';
import { usePendingQuestions } from '../hooks/usePendingQuestions';
import { PendingQuestionsModal } from './PendingQuestionsModal';

export const PendingQuestionsButton: React.FC = () => {
  const pq = usePendingQuestions();
  const [open, setOpen] = useState(false);

  // Hide entirely when not signed in, when questions are disabled,
  // while we haven't received the first snapshot yet (avoids a
  // brief flash on session start), or when there are no open
  // questions to answer.
  if (
    !auth.currentUser ||
    pq.isDisabled ||
    pq.isLoading ||
    pq.openCount === 0
  ) {
    return null;
  }

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <IconButton
        aria-label={`${pq.openCount} questions about your preferences`}
        onClick={handleOpen}
        size="medium"
      >
        <Badge
          badgeContent={pq.openCount > 9 ? '9+' : pq.openCount}
          color="primary"
        >
          <HelpOutlineRounded />
        </Badge>
      </IconButton>
      <PendingQuestionsModal
        open={open}
        onClose={handleClose}
        questions={pq.questions}
        onAnswer={pq.answer}
        onSkip={pq.skip}
        onDisable={() => pq.setEnabled(false)}
      />
    </>
  );
};
